import { useHead } from '@unhead/vue';
import type { Script } from '@unhead/schema';
import type { SeoGeneralData, SeoSpecificData } from '../../types/seo';

type UseStructuredDataParams = {
    scripts: Ref<Script[]>;
}

export function useStructuredData(params?: UseStructuredDataParams) {
    const { data: seoSpecificData } = useNuxtData<SeoSpecificData>('seoSpecificData');
    const { data: seoGeneralData } = useNuxtData<SeoGeneralData>('seoGeneralData');
    const url = useRequestURL();
    const { t } = useI18n();
    const localePath = useLocalePath();
    const { isHomePage } = useHomePage();
    const { breadcrumbs } = useBreadcrumbs();

    const organizationScriptElement: ComputedRef<Script | undefined> = computed(() => {
        if (!isHomePage.value) {
            return;
        }
        const socialLinks = [
            t('footer.socialMedia.instagramLink'),
            t('footer.socialMedia.facebookLink'),
            t('footer.socialMedia.linkedinLink'),
            t('footer.socialMedia.youtubeLink'),
        ].filter(Boolean);

        const organizationData = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: t('structuredData.organization.name'),
            url: `${url.origin}${localePath('/')}`,
            logo: `${url.origin}/logo.svg`,
            description: t('structuredData.organization.description'),
            email: t('structuredData.organization.email'),
            telephone: t('structuredData.organization.telephone'),
            address: {
                '@type': 'PostalAddress',
                streetAddress: t('structuredData.organization.address.streetAddress'),
                addressLocality: t('structuredData.organization.address.addressLocality'),
                addressCountry: t('structuredData.organization.address.addressCountry'),
                addressRegion: t('structuredData.organization.address.addressRegion'),
                postalCode: t('structuredData.organization.address.postalCode'),
            },
            vatID: t('structuredData.organization.vatID'),
            iso6523Code: t('structuredData.organization.iso6523Code'),
            sameAs: socialLinks.length ? socialLinks : undefined,
        };

        return {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(organizationData, (key, value) => {
                if (value === '' || value === null || value === undefined) {
                    return undefined;
                }
                return value;
            }),
            key: 'typeOrganization',
        };
    });

    const breadcrumbsScriptElement: ComputedRef<Script | undefined> = computed(() => {
        if (!breadcrumbs.value.length) {
            return;
        }

        const itemListElement = breadcrumbs.value.map((breadcrumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: breadcrumb.name,
            ...(breadcrumb.path && { item: `${url.origin}${breadcrumb.path}` }),
        }));

        return {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement,
            }),
            key: 'typeBreadcrumb',
        };
    });

    const storesScriptElements: ComputedRef<Script[]> = computed(() => {
        const specificStructuredData = seoSpecificData.value;

        if (!specificStructuredData?.stores) {
            return [];
        }

        return specificStructuredData.stores.map(store => ({
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                name: store.name,
                image: url.href,
                '@id': store.id,
                url: store.url,
                telephone: store.phone,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: store.street,
                    addressLocality: store.city,
                    addressRegion: store.countryState,
                    postalCode: store.zipcode,
                    addressCountry: store.country,
                },
                openingHoursSpecification: store.openingHours,
            }),
            key: `typeStore${ store.id }`,
        }));
    });

    const faqScriptElement: ComputedRef<Script | undefined> = computed(() => {
        const specificStructuredData = seoSpecificData.value;

        if (!specificStructuredData?.faqs) {
            return;
        }

        const questionsScripts = specificStructuredData.faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        }));

        return {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: questionsScripts,
            }),
            key: 'typeFaq',
        };
    });

    const scripts: ComputedRef<Script[]> = computed(() => {
        const scripts: Script[] = [];

        if (organizationScriptElement.value) {
            scripts.push(organizationScriptElement.value);
        }

        if (breadcrumbsScriptElement.value) {
            scripts.push(breadcrumbsScriptElement.value);
        }

        scripts.push(...storesScriptElements.value);

        if (faqScriptElement.value) {
            scripts.push(faqScriptElement.value);
        }

        if (params?.scripts.value) {
            scripts.push(...params.scripts.value);
        }

        return scripts;
    });

    useHead({
        script: scripts,
    });
}
